import { useEffect } from 'react';
import styles from './GroupPage.module.css';
import { FaCheck, FaTimes, FaQuestion, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Table from '../../../Components/Table/Table';
import UpdateWorkout from '../../AthletePage/CalendarPage/UpdateWorkout';
import { useState, useMemo } from 'react';
import API from '../../../Utils/api';
import { toast } from 'react-toastify';
import { Link } from 'react-router';

const StatusIcon = ({ status }) => {
    if (!status) {
        return <FaTimes className={styles.attendanceDeclined} />;
    }

    switch (status) {
        case 'Completed':
            return <FaCheck className={styles.attendanceCompleted} />;
        case 'Assigned':
            return <FaQuestion className={styles.attendanceAssigned} />;
        default:
            return <FaTimes className={styles.attendanceDeclined} />;
    }
};

const AttendanceSheet = ({ programGroups = [], athletes = [], onAttendanceUpdate }) => {
    const [selectedWorkout, setSelectedWorkout] = useState(null);
    const [loadingWorkout, setLoadingWorkout] = useState(false);
    const [currentWeekKey, setCurrentWeekKey] = useState(null);

    const weeklyGroups = useMemo(() => {
        const weeks = {};
        programGroups.forEach(pg => {
            const date = new Date(pg.date);
            const weekStart = new Date(date);
            // Get day of week (0 = Sunday, 1 = Monday, etc.)
            const day = date.getDay();
            // Calculate offset to Monday (if Sunday, go back 6 days; otherwise go back (day - 1) days)
            const offset = day === 0 ? 6 : day - 1;
            weekStart.setDate(date.getDate() - offset);
            const weekKey = weekStart.toISOString().split('T')[0];

            if (!weeks[weekKey]) {
                weeks[weekKey] = {
                    weekStart,
                    groups: []
                };
            }
            weeks[weekKey].groups.push(pg);
        });

        return Object.entries(weeks)
            .map(([key, value]) => ({ ...value, key }))
            .sort((a, b) => a.weekStart - b.weekStart);
    }, [programGroups]);

    useEffect(() => {
        if (!weeklyGroups.length) return;
        const stillExists = weeklyGroups.some(w => w.key === currentWeekKey);
        if (!currentWeekKey || !stillExists) {
            setCurrentWeekKey(weeklyGroups[weeklyGroups.length - 1].key);
        }
    }, [weeklyGroups, currentWeekKey]);

    const derivedWeekIndex =
        weeklyGroups.length && weeklyGroups.findIndex(w => w.key === currentWeekKey) === -1
            ? weeklyGroups.length - 1
            : weeklyGroups.findIndex(w => w.key === currentWeekKey);
    const currentWeekIndex = derivedWeekIndex === -1 ? 0 : derivedWeekIndex;
    const currentWeek = weeklyGroups[currentWeekIndex] || { groups: [], key: null };

    const handleCellClick = async workoutId => {
        if (!workoutId) {
            toast.error('No workout found for this athlete and program group');
            return;
        }

        setLoadingWorkout(true);
        try {
            const response = await API.getWorkoutById(workoutId);
            setSelectedWorkout(response.data);
        } catch (error) {
            toast.error('Failed to load workout data');
        } finally {
            setLoadingWorkout(false);
        }
    };

    const handleCloseModal = () => {
        setSelectedWorkout(null);
    };

    const handleWorkoutSuccess = (message, data) => {
        toast.success(message);
        if (onAttendanceUpdate) {
            onAttendanceUpdate();
        }
        setSelectedWorkout(null);
    };

    const handleWorkoutFailure = message => {
        toast.error(message);
    };

    const formatWeekRange = weekStart => {
        const endOfWeek = new Date(weekStart);
        endOfWeek.setDate(weekStart.getDate() + 6);

        const startStr = weekStart.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short'
        });
        const endStr = endOfWeek.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });

        return `${startStr} - ${endStr}`;
    };

    const getWeekOfYear = date => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));

        const week1 = new Date(d.getFullYear(), 0, 4);

        return (
            1 +
            Math.round(
                ((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7
            )
        );
    };

    const goToPreviousWeek = () => {
        if (currentWeekIndex > 0) {
            setCurrentWeekKey(weeklyGroups[currentWeekIndex - 1].key);
        }
    };

    const goToNextWeek = () => {
        if (currentWeekIndex < weeklyGroups.length - 1) {
            setCurrentWeekKey(weeklyGroups[currentWeekIndex + 1].key);
        }
    };

    const columns = [
        {
            Header: 'Athlete Name',
            accessor: 'name',
            id: 'athlete',
            Cell: ({ row }) => (
                <Link
                    to={`/athlete/${row.original.id}/profile`}
                    className={styles.athleteLink}
                >
                    {row.original.name}
                </Link>
            )
        },
        ...currentWeek.groups.map(pg => ({
            Header: () => {
                const date = new Date(pg.date);
                const dateStr = date.toLocaleDateString('en-GB');
                const timeStr = date.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                return (
                    <div>
                        {pg.name} ({dateStr} {timeStr})
                    </div>
                );
            },
            accessor: athlete => {
                const attendanceData = athlete.attendance?.[pg.id];
                return attendanceData?.attendance?.status || 'Declined';
            },
            id: pg.id,
            Cell: ({ value, row }) => {
                const attendanceData = row.original.attendance?.[pg.id];
                const workoutId = attendanceData?.workoutId;
                const status = attendanceData?.attendance;

                return (
                    <div
                        className={styles.clickableAttendanceCell}
                        onClick={() => handleCellClick(workoutId)}
                        title={workoutId ? 'Click to update attendance' : 'No workout available'}
                        style={{
                            opacity: workoutId ? 1 : 0.5
                        }}
                    >
                        {loadingWorkout ? (
                            <div className={styles.loadingSpinner}>...</div>
                        ) : (
                            <StatusIcon status={status} />
                        )}
                    </div>
                );
            }
        })),
        {
            Header: 'Attendance',
            id: 'attendanceSummary',
            accessor: athlete => {
                const currentWeekWorkouts = currentWeek.groups.length;
                const currentWeekCompleted = currentWeek.groups.filter(pg => {
                    const attendanceData = athlete.attendance?.[pg.id];
                    return attendanceData?.attendance === 'Completed';
                }).length;

                const totalWorkouts = programGroups.length;
                const totalCompleted = programGroups.filter(pg => {
                    const attendanceData = athlete.attendance?.[pg.id];
                    return attendanceData?.attendance === 'Completed';
                }).length;

                return {
                    currentWeek: `${currentWeekCompleted}/${currentWeekWorkouts}`,
                    total: `${totalCompleted}/${totalWorkouts}`
                };
            },
            Cell: ({ value }) => (
                <div className={styles.attendanceSummary}>
                    <div>{value.currentWeek}</div>
                    <div className={styles.attendanceSummaryTotal}>({value.total})</div>
                </div>
            )
        }
    ];

    if (weeklyGroups.length === 0) {
        return (
            <div className={styles.attendanceSheet}>
                <div className={styles.emptyState}>
                    No program groups found for attendance tracking.
                </div>
            </div>
        );
    }

    return (
        <div className={styles.attendanceSheet}>
            <div className={styles.weekNavigation}>
                <button
                    onClick={goToPreviousWeek}
                    disabled={currentWeekIndex === 0}
                    className={styles.weekNavigationButton}
                >
                    <FaChevronLeft />
                </button>

                <div className={styles.weekNavigationCenter}>
                    <h3 className={styles.weekNavigationTitle}>
                        Week {getWeekOfYear(currentWeek.weekStart)}
                    </h3>
                    <span className={styles.weekNavigationDate}>
                        {formatWeekRange(currentWeek.weekStart)}
                    </span>
                </div>

                <button
                    onClick={goToNextWeek}
                    disabled={currentWeekIndex === weeklyGroups.length - 1}
                    className={styles.weekNavigationButton}
                >
                    <FaChevronRight />
                </button>
            </div>

            <Table columns={columns} data={athletes} />

            <div className={styles.attendanceLegend}>
                <div className={styles.legendItem}>
                    <FaCheck className={styles.attendanceCompleted} /> Present
                </div>
                <div className={styles.legendItem}>
                    <FaTimes className={styles.attendanceDeclined} /> Absent
                </div>
                <div className={styles.legendItem}>
                    <FaQuestion className={styles.attendanceAssigned} /> Pending
                </div>
            </div>

            {selectedWorkout && (
                <UpdateWorkout
                    data={selectedWorkout}
                    closeCB={handleCloseModal}
                    onSuccess={handleWorkoutSuccess}
                    onFailure={handleWorkoutFailure}
                    hideDeleteButton={true}
                    athleteCSS={selectedWorkout.css}
                    athleteMSS={selectedWorkout.mss}
                />
            )}
        </div>
    );
};

export default AttendanceSheet;
